import { useState, useEffect } from 'react';
import { Table as AntTable, Checkbox, Flex, Tooltip } from 'antd';
import { useFeatureEntitlements } from '@frontegg/react';
import { useSyncEhrAppointmentsByOrg } from 'src/hooks/ehr/useSyncEhrAppointmentsByOrg';
import { useSyncEhrAppointmentsByProvider } from 'src/hooks/ehr/useSyncEhrAppointmentsByProvider';
import { useGetOrganizationEhrSettings } from 'src/hooks/settings';
import { classNames } from 'src/utilities';
import { ModalComponent } from './Modal';
import { AlertIcon } from '../../../../icons';
import {
  listEncounters,
  // listPinnedEncounters,
  removeEncounter,
} from '../../store/actions/encounter';
import { useDispatch, useSelector } from 'react-redux';
import { showToastSuccess } from '../../../../utilities/toast';
import { showToastError } from '../../../../utilities/errortoast';
import { Button } from '@insight-health-ai/roseate/components';
import { RefreshCcw01 } from '@insight-health-ai/roseate/icons';
import sortUp from '/src/assets/sortUp.svg';
import sortDown from '/src/assets/sortDown.svg';
import { selectedEncounterSlice } from '../../store/slices/encounter';
import { useSelectedEncounter } from 'src/hooks/embedded/useSelectedEncounter';

export const EncounterTable = ({
  className,
  rowClassName,
  columns,
  rowKey,
  loading,
  onRow,
  dataSource,
  setSearchFilters,
  setStartDate,
  setEndDate,
  storedParams,
  selectedRowKeys,
  setSelectedRowKeys,
  handleEncountersSorting,
  sortedInfo,
  isUrgent = false,
  title,
}) => {
  const dispatch = useDispatch();
  const [tableColumns, setTableColumns] = useState([]);
  const [isDeleteMultipleModalVisible, setisDeleteMultipleModalVisible] =
    useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const {
    mutate: syncEhrAppointments,
    isPending: isSyncEhrAppointmentsPending,
  } = useSyncEhrAppointmentsByOrg();
  const {
    mutate: syncEhrAppointmentsByProvider,
    isPending: isSyncByProviderPending,
  } = useSyncEhrAppointmentsByProvider();
  const { ehrSettings } = useGetOrganizationEhrSettings();
  const { isEntitled: isProviderSyncEnabled } = useFeatureEntitlements(
    'provider-specific-ehr-sync'
  );

  const isSyncPending = isSyncEhrAppointmentsPending || isSyncByProviderPending;

  // Selected encounter storage (uses sessionStorage for window isolation)
  const { getSelectedEncounter, clearSelectedEncounter } = useSelectedEncounter(
    currentPractitioner?.org_uuid
  );

  const handleSyncEhrAppointments = () => {
    if (!currentPractitioner?.org_uuid || !ehrSettings?.ehr_system) return;

    const ehrId = currentPractitioner?.practitioner_ehr_id;
    const useProviderSync = isProviderSyncEnabled && ehrId;

    if (useProviderSync) {
      syncEhrAppointmentsByProvider(
        {
          org_uuid: currentPractitioner.org_uuid,
          ehr: ehrSettings.ehr_system,
          provider_ehr_id: ehrId,
        },
        {
          onSuccess: () => {
            showToastSuccess('EHR appointments synced successfully');
            dispatch(listEncounters(storedParams, true));
          },
          onError: () => {
            showToastError('Failed to sync EHR appointments');
          },
        }
      );
    } else {
      syncEhrAppointments(
        {
          org_uuid: currentPractitioner.org_uuid,
          ehr: ehrSettings.ehr_system,
        },
        {
          onSuccess: () => {
            showToastSuccess('EHR appointments synced successfully');
            dispatch(listEncounters(storedParams, true));
          },
          onError: () => {
            showToastError('Failed to sync EHR appointments');
          },
        }
      );
    }
  };

  const getSelectedRowDetails = () => {
    const selectedRows = dataSource.filter((row) =>
      selectedRowKeys?.includes(row.encounter_id)
    );
    return selectedRows;
  };

  const handleDelete = () => {
    setisDeleteMultipleModalVisible(true);
  };

  useEffect(() => {
    // Here you can set the width for each column
    const modifiedColumns = columns.map((column) => ({
      ...column,
      width: 2000, // Set your desired width here
    }));
    const columnsWithCheckbox = [
      ...modifiedColumns, // Existing columns
      {
        title: '',
        key: 'selection',
        render: (_, record) => (
          <div className="checkbox-container pr-1">
            <Checkbox
              checked={selectedRowKeys?.includes(record.encounter_id)}
              onChange={(e) => {
                const newSelectedKeys = e.target.checked
                  ? [...selectedRowKeys, record.encounter_id] // Add encounter_id
                  : selectedRowKeys?.filter((id) => id !== record.encounter_id); // Remove encounter_id
                setSelectedRowKeys(newSelectedKeys);
              }}
            />
          </div>
        ),
      },
    ];
    setTableColumns(columnsWithCheckbox);
    // TODO: FIX this use effect dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, setSelectedRowKeys]);

  const hasSelected = selectedRowKeys?.length > 0;

  const handleDeleteMultipleEncounters = async () => {
    const selectedRows = getSelectedRowDetails();
    if (!selectedRows.length) {
      return;
    }
    setSubmitLoading(true);
    const selectedEncounter = getSelectedEncounter();

    try {
      for (const row of selectedRows) {
        if (row.encounter_id === selectedEncounter?.encounter_id) {
          clearSelectedEncounter();
        }
        await dispatch(
          removeEncounter(
            row.encounter_id, //  'encounter_id' is the unique identifier for each row
            storedParams,
            setSubmitLoading,
            false
          )
        );
      }
      showToastSuccess('Removed encounter(s)');
    } catch (error) {
      console.error('Error deleting encounter(s):', error);
      showToastError('Failed to delete encounter(s)');
    } finally {
      setSubmitLoading(false);
      dispatch(selectedEncounterSlice(null));
      dispatch(listEncounters(storedParams, true));
      // dispatch(listPinnedEncounters(storedParams, true));
      // Clear the selected row keys
      setSelectedRowKeys([]);
      setisDeleteMultipleModalVisible(false);
    }
  };

  const handleCancel = () => {
    setSubmitLoading(false);
    setisDeleteMultipleModalVisible(false);
  };

  return (
    <>
      <div className={classNames('', className ? className : '')}>
        {title}
        <Flex vertical>
          <div
            className={
              isUrgent
                ? 'pl-2 md:pl-0 md:pb-2'
                : `sticky top-0 card-title bg-[#fff] text-base flex justify-between z-0 md:z-10 p-2 shadow-sm empty:hidden`
            }
          >
            <div
              className={`flex items-center gap-6 ${isUrgent ? 'justify-end' : ''}`}
            >
              {!isUrgent && dataSource?.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-[#000000e0]">
                  <Tooltip
                    title={
                      sortedInfo?.order === 'asc'
                        ? 'Oldest first'
                        : 'Newest first'
                    }
                    placement="topLeft"
                  >
                    <div
                      onClick={handleEncountersSorting}
                      className="border-[1.5px] border-gray-400 rounded cursor-pointer"
                    >
                      <img
                        src={sortedInfo?.order === 'desc' ? sortUp : sortDown}
                        alt={
                          sortedInfo?.order === 'desc' ? 'sort-up' : 'sort-down'
                        }
                        height={18}
                        width={18}
                      />
                    </div>
                  </Tooltip>
                  Sort
                </div>
              )}
            </div>
            <div
              className={`flex items-center gap-6 ${isUrgent ? 'justify-end' : ''}`}
            >
              {ehrSettings?.ehr_system && !isUrgent && (
                <Tooltip title="Sync EHR Encounters">
                  <Button
                    appearance="secondary"
                    onClick={handleSyncEhrAppointments}
                    disabled={isSyncPending || !ehrSettings?.ehr_system}
                    loading={isSyncPending}
                    icon={<RefreshCcw01 className="!text-[#5667E9]" />}
                    className="!border-0 !bg-transparent !text-[#5667E9]"
                  />
                </Tooltip>
              )}
              {hasSelected ? (
                <Button
                  appearance="danger"
                  onClick={handleDelete}
                  className="!text-xs !h-8"
                >
                  {`Delete ${selectedRowKeys?.length} item(s)`}
                </Button>
              ) : null}
            </div>
          </div>

          <AntTable
            columns={tableColumns}
            rowClassName={(record) =>
              classNames(
                typeof rowClassName === 'function'
                  ? rowClassName(record)
                  : rowClassName,
                'cursor-pointer'
              )
            }
            dataSource={dataSource}
            loading={loading}
            pagination={false}
            showHeader={false}
            onRow={onRow}
            rowKey={rowKey}
            className={classNames(
              'cursor-pointer',
              isUrgent ? '[&_.ant-table-row]:mb-2' : ''
            )}
            locale={{
              emptyText: (
                <div className="flex flex-col justify-center items-center">
                  <span className="text-center mb-3 ">
                    {setStartDate && setEndDate && setSearchFilters
                      ? 'No Data'
                      : ''}
                  </span>
                </div>
              ),
            }}
          />
        </Flex>
      </div>
      <ModalComponent
        centered={true}
        open={isDeleteMultipleModalVisible}
        handleSubmit={handleDeleteMultipleEncounters}
        handleCancel={handleCancel}
        submitButtonText={`Delete ${selectedRowKeys?.length} item(s)`}
        cancelButtonText="Cancel"
        closable={false}
        className="!w-fit"
        submitLoading={submitLoading}
        additionalBody={
          <>
            {' '}
            <div className="text-black font-sans font-medium text-lg flex justify-center items-center gap-2">
              <span>
                {' '}
                <AlertIcon />
              </span>
              <p>
                This will permanently delete the encounters. Are you sure you
                want to delete?
              </p>
            </div>
          </>
        }
      />
    </>
  );
};
